/**
 * 새 탭 슬라이드 뷰: 키로 본문 전달.
 * placeholder 탭이 있으면 해당 탭 sessionStorage에 저장(전역 LS quota 회피).
 * 동기 열기·레거시 호환은 localStorage에도 시도한다.
 */
import {
  parseSlideLayoutJson,
  slideLayoutHasMeaningfulCanvasContent,
  type ProgramContentSlideOwnerRef,
  type ProgramSlideLayoutV1,
} from '../types/programContentSlideCanvas';
import { isPersistableUuid } from './programContentSlideOwner';
import { buildAppWindowPlaceholderUrl } from './appDocumentTitle';
import { openPresentationWindow } from './openPresentationWindow';
import { writeProgramContentSlideLearningOverlay } from './programContentSlideLearningOverlay';

const STORAGE_PREFIX = 'cp.programContentSlide.v1:';
const SLIDE_STASH_LS_MAX = 4;
export const PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_KEY = 'cp:program-content-slide-learning-updated:v1';
/** 같은 창에서 `storage` 이벤트가 나지 않을 때(슬라이드 탭·프로그램 탭 동시 표시 등) 프로그램 표가 갱신되도록 */
export const PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_EVENT = 'cp:program-content-slide-learning-updated';
/** 고정 프로그램 행 순서 변경으로 LS 페이로드 `owner.id`가 바뀐 뒤 슬라이드 탭이 다시 읽도록 알림 */
export const PROGRAM_CONTENT_SLIDE_OWNER_REMAPPED_EVENT = 'cp:program-content-slide-owner-remapped';

/** 프로그램 표에 반영 완료 후 호출 — 탭 복귀 시 같은 페이로드가 반복 적용되어 행/병합 상태가 흔들리지 않게 함 */
export function clearProgramContentSlideLearningUpdatedFlag(): void {
  try {
    slideStorage()?.removeItem(PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_KEY);
  } catch {
    /* ignore */
  }
}

function slideStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function parseSlideOwner(raw: unknown): ProgramContentSlideOwnerRef | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as { kind?: unknown; id?: unknown };
  const id = String(o.id ?? '').trim();
  const kind = o.kind;
  if (!id) return null;
  if (kind === 'program_cell' || kind === 'fixed_row' || kind === 'scoped_row' || kind === 'presentation_ref') {
    return { kind, id };
  }
  return null;
}

export interface ProgramContentSlidePayloadV1 {
  v: 1;
  workspaceId: string;
  /** 프로그램 페이지 워크스페이스 scopeId (통합 저장 대상 식별) */
  scopeId?: string;
  /** 프로그램 표에서 프레젠테이션을 연 행(학습내용 셀). 탭 락 오버레이·해제 시 행 단위 집계에 사용 */
  annualCellKey?: string;
  /**
   * 클래스 멤버(뽑기 번호 범위 등) 조회용 학년 ID.
   * `createPresentationRefOnSave`가 없어도(이미 저장된 슬라이드) 뽑기에서 그룹 멤버 수를 맞출 때 사용.
   */
  contextGradeId?: string;
  academicYear: number;
  termNumber: 1 | 2;
  /** DB에 슬라이드 캔버스를 묶을 행(null이면 추가 페이지는 로컬만·저장 불가) */
  owner: ProgramContentSlideOwnerRef | null;
  /** learning_text·presentationRefId가 저장되는 프로그램 행(scoped_row 등) */
  learningTextOwner?: ProgramContentSlideOwnerRef | null;
  /** 탭을 열 때의 학습내용 원문 — 표 초안과 DB가 어긋날 때 ref 해제에 사용 */
  bindingLearningText?: string;
  createPresentationRefOnSave?: {
    annualKey: string;
    learningText: string;
    saveMode?: 'program_cells' | 'fixed_content';
    mode?: 'default' | 'fixed_to_unit' | 'project_to_template';
    gradeId?: string;
    mondayIso?: string;
    weekdayKey?: string;
    slotIndex?: number;
    orderIndex?: number;
    sourceAnnualCellKey?: string | null;
    timetableText?: string;
    unitText?: string;
    pageText?: string;
    assistantPageText?: string;
    currentPeriodText?: string;
    totalPeriodText?: string;
    materialText?: string;
    recordCrossCurricularText?: string;
    recordMemoText?: string;
    recordEvaluationText?: string;
    recordClassText?: string;
  };
  /** 최소 추가 슬라이드 개수(레거시). 캔버스 도입 후에도 기본 1 유지 */
  extraSlideBodies: string[];
  /**
   * 학습계획으로 만든 미리보기 슬라이드.
   * 탭을 연 동안만 보이고, 저장을 눌러야 DB에 남는다. 닫으면 버려진다.
   */
  aiPreviewLayouts?: ProgramSlideLayoutV1[];
  meta: {
    templateLabel: string;
    unit: string;
    page: string;
    assistantPage: string;
    programContent: string;
    /** 단원 팝업 성취기준(여러 줄) */
    achievementText: string;
    /** 학습내용 셀 메모(학습메모 탭) */
    learningMemoNote?: string;
    /** 평가 셀 메모(평가계획 탭) */
    evaluationMemoNote?: string;
    /** 주간학습 안내 시간표 과목행과 동일한 편제 배경색(#RRGGBB) */
    weeklyGuideTrackColorHex?: string;
  };
}

function slideStashKey(id: string): string {
  return STORAGE_PREFIX + id;
}

function newSlideStashId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}

function pruneOldSlideStashEntries(): void {
  const ls = slideStorage();
  if (!ls) return;
  const keys: string[] = [];
  for (let i = 0; i < ls.length; i += 1) {
    const key = ls.key(i);
    // 학교·노트 RQ 캐시(cp.reactQuery.*, cp.programFixed* 등)는 절대 건드리지 않는다.
    if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
  }
  if (keys.length <= SLIDE_STASH_LS_MAX) return;
  keys.sort();
  for (const key of keys.slice(0, keys.length - SLIDE_STASH_LS_MAX)) {
    try {
      ls.removeItem(key);
    } catch {
      /* ignore */
    }
  }
}

function writeSlideStashPayload(key: string, serialized: string, targetTab?: Window | null): boolean {
  let wrote = false;
  if (targetTab && !targetTab.closed) {
    try {
      targetTab.sessionStorage.setItem(key, serialized);
      wrote = true;
    } catch {
      /* opener→자식 sessionStorage 불가 브라우저 — LS 백업 */
    }
  }
  const ls = slideStorage();
  if (ls) {
    pruneOldSlideStashEntries();
    try {
      ls.setItem(key, serialized);
      wrote = true;
    } catch {
      /* quota 등 */
    }
  }
  return wrote;
}

function readSlideStashRaw(id: string): string | null {
  if (!id.trim() || typeof window === 'undefined') return null;
  const key = slideStashKey(id);
  try {
    return window.sessionStorage.getItem(key) ?? window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function parseAiPreviewLayouts(raw: unknown): ProgramSlideLayoutV1[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: ProgramSlideLayoutV1[] = [];
  for (const item of raw) {
    const layout = parseSlideLayoutJson(item);
    if (!slideLayoutHasMeaningfulCanvasContent(layout)) continue;
    out.push(layout);
    if (out.length >= 12) break;
  }
  return out.length ? out : undefined;
}

function parseProgramContentSlidePayloadRaw(raw: string): ProgramContentSlidePayloadV1 | null {
  try {
    const o = JSON.parse(raw) as ProgramContentSlidePayloadV1;
    if (!o || o.v !== 1 || !o.meta) return null;
    const termRaw = Number((o as { termNumber?: unknown }).termNumber);
    const termNumber: 1 | 2 = termRaw === 2 ? 2 : 1;
    const workspaceId = String((o as { workspaceId?: unknown }).workspaceId ?? '');
    const academicYearRaw = Number((o as { academicYear?: unknown }).academicYear);
    const academicYear = Number.isFinite(academicYearRaw) ? academicYearRaw : 0;
    return {
      v: 1,
      workspaceId,
      scopeId: String((o as { scopeId?: unknown }).scopeId ?? '').trim() || undefined,
      annualCellKey: String((o as { annualCellKey?: unknown }).annualCellKey ?? '').trim() || undefined,
      contextGradeId:
        String((o as { contextGradeId?: unknown }).contextGradeId ?? '').trim() || undefined,
      academicYear,
      termNumber,
      owner: 'owner' in (o as object) ? parseSlideOwner((o as { owner?: unknown }).owner) : null,
      learningTextOwner:
        'learningTextOwner' in (o as object)
          ? parseSlideOwner((o as { learningTextOwner?: unknown }).learningTextOwner)
          : undefined,
      bindingLearningText:
        typeof (o as { bindingLearningText?: unknown }).bindingLearningText === 'string'
          ? String((o as { bindingLearningText?: unknown }).bindingLearningText)
          : undefined,
      createPresentationRefOnSave:
        (o as { createPresentationRefOnSave?: unknown }).createPresentationRefOnSave &&
        typeof (o as { createPresentationRefOnSave?: { annualKey?: unknown; learningText?: unknown } }).createPresentationRefOnSave === 'object'
          ? {
              annualKey: String(
                (o as { createPresentationRefOnSave: { annualKey?: unknown } }).createPresentationRefOnSave.annualKey ??
                  ''
              ),
              learningText: String(
                (o as { createPresentationRefOnSave: { learningText?: unknown } }).createPresentationRefOnSave.learningText ??
                  ''
              ),
              saveMode:
                (o as { createPresentationRefOnSave: { saveMode?: unknown } }).createPresentationRefOnSave.saveMode ===
                'fixed_content'
                  ? 'fixed_content'
                  : 'program_cells',
              mode:
                (o as { createPresentationRefOnSave: { mode?: unknown } }).createPresentationRefOnSave.mode ===
                  'fixed_to_unit' ||
                (o as { createPresentationRefOnSave: { mode?: unknown } }).createPresentationRefOnSave.mode ===
                  'project_to_template'
                  ? ((o as { createPresentationRefOnSave: { mode: 'fixed_to_unit' | 'project_to_template' } })
                      .createPresentationRefOnSave.mode)
                  : 'default',
              gradeId: String(
                (o as { createPresentationRefOnSave: { gradeId?: unknown } }).createPresentationRefOnSave.gradeId ?? ''
              ),
              mondayIso: String(
                (o as { createPresentationRefOnSave: { mondayIso?: unknown } }).createPresentationRefOnSave.mondayIso ??
                  ''
              ),
              weekdayKey: String(
                (o as { createPresentationRefOnSave: { weekdayKey?: unknown } }).createPresentationRefOnSave.weekdayKey ??
                  ''
              ),
              slotIndex: Number(
                (o as { createPresentationRefOnSave: { slotIndex?: unknown } }).createPresentationRefOnSave.slotIndex ?? 0
              ),
              orderIndex: Number(
                (o as { createPresentationRefOnSave: { orderIndex?: unknown } }).createPresentationRefOnSave.orderIndex ?? 0
              ),
              sourceAnnualCellKey:
                (o as { createPresentationRefOnSave: { sourceAnnualCellKey?: unknown } }).createPresentationRefOnSave
                  .sourceAnnualCellKey == null
                  ? null
                  : String(
                      (o as { createPresentationRefOnSave: { sourceAnnualCellKey?: unknown } })
                        .createPresentationRefOnSave.sourceAnnualCellKey ?? ''
                    ),
              timetableText: String(
                (o as { createPresentationRefOnSave: { timetableText?: unknown } }).createPresentationRefOnSave
                  .timetableText ?? ''
              ),
              unitText: String(
                (o as { createPresentationRefOnSave: { unitText?: unknown } }).createPresentationRefOnSave.unitText ?? ''
              ),
              pageText: String(
                (o as { createPresentationRefOnSave: { pageText?: unknown } }).createPresentationRefOnSave.pageText ?? ''
              ),
              assistantPageText: String(
                (o as { createPresentationRefOnSave: { assistantPageText?: unknown } }).createPresentationRefOnSave
                  .assistantPageText ?? ''
              ),
              currentPeriodText: String(
                (o as { createPresentationRefOnSave: { currentPeriodText?: unknown } }).createPresentationRefOnSave
                  .currentPeriodText ?? ''
              ),
              totalPeriodText: String(
                (o as { createPresentationRefOnSave: { totalPeriodText?: unknown } }).createPresentationRefOnSave
                  .totalPeriodText ?? ''
              ),
              materialText: String(
                (o as { createPresentationRefOnSave: { materialText?: unknown } }).createPresentationRefOnSave.materialText ??
                  ''
              ),
              recordCrossCurricularText: String(
                (o as { createPresentationRefOnSave: { recordCrossCurricularText?: unknown } }).createPresentationRefOnSave
                  .recordCrossCurricularText ?? ''
              ),
              recordMemoText: String(
                (o as { createPresentationRefOnSave: { recordMemoText?: unknown } }).createPresentationRefOnSave
                  .recordMemoText ?? ''
              ),
              recordEvaluationText: String(
                (o as { createPresentationRefOnSave: { recordEvaluationText?: unknown } }).createPresentationRefOnSave
                  .recordEvaluationText ?? ''
              ),
              recordClassText: String(
                (o as { createPresentationRefOnSave: { recordClassText?: unknown } }).createPresentationRefOnSave
                  .recordClassText ?? ''
              ),
            }
          : undefined,
      extraSlideBodies: Array.isArray(o.extraSlideBodies)
        ? o.extraSlideBodies.map((s) => String(s ?? ''))
        : [],
      aiPreviewLayouts: parseAiPreviewLayouts((o as { aiPreviewLayouts?: unknown }).aiPreviewLayouts),
      meta: {
        templateLabel: String(o.meta.templateLabel ?? ''),
        unit: String(o.meta.unit ?? ''),
        page: String(o.meta.page ?? ''),
        assistantPage: String(o.meta.assistantPage ?? ''),
        programContent: String(o.meta.programContent ?? ''),
        achievementText: String(o.meta.achievementText ?? ''),
        learningMemoNote:
          typeof o.meta.learningMemoNote === 'string' ? String(o.meta.learningMemoNote) : undefined,
        evaluationMemoNote:
          typeof o.meta.evaluationMemoNote === 'string' ? String(o.meta.evaluationMemoNote) : undefined,
        weeklyGuideTrackColorHex:
          typeof o.meta.weeklyGuideTrackColorHex === 'string' ? o.meta.weeklyGuideTrackColorHex.trim() : undefined,
      },
    };
  } catch {
    return null;
  }
}

export function stashProgramContentSlidePayload(
  payload: ProgramContentSlidePayloadV1,
  targetTab?: Window | null
): string {
  const id = newSlideStashId();
  const key = slideStashKey(id);
  const serialized = JSON.stringify(payload);
  if (writeSlideStashPayload(key, serialized, targetTab)) return id;
  return '';
}

/**
 * 고정 프로그램(`fixed_content`) 행 재정렬 시 워크스페이스 행 id가 슬롯 기준으로 바뀐다.
 * 열린 슬라이드 탭 LS 페이로드의 `owner`(scoped_row 등)를 그에 맞게 치환한다 — 저장 시 엉뚱한 DB 행을 가리키지 않게 함.
 */
export function remapProgramContentSlideStashOwnersAfterFixedReorder(params: {
  scopeId: string;
  workspaceRows: Array<{ id?: string | null }>;
  posToOld: number[];
}): void {
  const scopeIdTrim = (params.scopeId ?? '').trim();
  const ws = params.workspaceRows;
  const posToOld = params.posToOld;
  if (!scopeIdTrim || !ws.length || !posToOld.length) return;
  const n = Math.min(ws.length, posToOld.length);
  const π = new Map<string, string>();
  for (let newIdx = 0; newIdx < n; newIdx += 1) {
    const oldIdx = posToOld[newIdx];
    if (oldIdx === newIdx) continue;
    const fromId = String(ws[oldIdx]?.id ?? '').trim();
    const toId = String(ws[newIdx]?.id ?? '').trim();
    if (fromId && toId && fromId !== toId && isPersistableUuid(fromId) && isPersistableUuid(toId)) {
      π.set(fromId, toId);
    }
  }
  if (!π.size) return;

  const ls = slideStorage();
  if (!ls) return;
  let any = false;
  try {
    for (let i = 0; i < ls.length; i += 1) {
      const key = ls.key(i);
      if (!key?.startsWith(STORAGE_PREFIX)) continue;
      const raw = ls.getItem(key);
      if (!raw) continue;
      let parsed: ProgramContentSlidePayloadV1;
      try {
        parsed = JSON.parse(raw) as ProgramContentSlidePayloadV1;
      } catch {
        continue;
      }
      if (!parsed || parsed.v !== 1 || (parsed.scopeId ?? '').trim() !== scopeIdTrim) continue;
      const o = parsed.owner;
      if (
        o &&
        (o.kind === 'scoped_row' || o.kind === 'fixed_row' || o.kind === 'program_cell') &&
        o.id
      ) {
        const nextId = π.get(o.id.trim());
        if (nextId) {
          parsed.owner = { kind: o.kind, id: nextId };
          ls.setItem(key, JSON.stringify(parsed));
          any = true;
        }
      }
    }
  } catch {
    /* ignore */
  }
  if (any && typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent(PROGRAM_CONTENT_SLIDE_OWNER_REMAPPED_EVENT));
    } catch {
      /* ignore */
    }
  }
}

export function readProgramContentSlidePayload(id: string): ProgramContentSlidePayloadV1 | null {
  const raw = readSlideStashRaw(id);
  if (!raw) return null;
  return parseProgramContentSlidePayloadRaw(raw);
}

export function updateProgramContentSlidePayloadOwner(
  id: string,
  owner: ProgramContentSlideOwnerRef,
  createPresentationRefOnSave?: undefined
): void {
  if (!id.trim()) return;
  const key = slideStashKey(id);
  try {
    const raw = readSlideStashRaw(id);
    if (!raw) return;
    const parsed = JSON.parse(raw) as ProgramContentSlidePayloadV1;
    const next = { ...parsed, owner };
    if (createPresentationRefOnSave === undefined) {
      delete (next as { createPresentationRefOnSave?: unknown }).createPresentationRefOnSave;
    }
    const serialized = JSON.stringify(next);
    try {
      window.sessionStorage.setItem(key, serialized);
    } catch {
      /* ignore */
    }
    try {
      slideStorage()?.setItem(key, serialized);
    } catch {
      /* ignore */
    }
  } catch {
    /* ignore */
  }
}

/**
 * 슬라이드 탭·프로그램 표 간 학습내용 동기 알림.
 * INVARIANT: 표에 반영·건너뛴 뒤 `clearProgramContentSlideLearningUpdatedFlag()` 필수.
 * @see PROGRAM_LEARNING_ATTACHMENT_INVARIANTS in dataTransferAudit.ts
 */
export function notifyProgramContentSlideLearningUpdated(params: {
  workspaceId: string;
  scopeId?: string;
  annualKey?: string;
  learningText: string;
  /** 행 순서 변경 후에도 올바른 셀에 반영되도록 */
  presentationRefId?: string;
  /** DB 행 식별 — 프로그램 표에서 학습내용 패치 위치를 찾을 때 필수 */
  owner?: ProgramContentSlideOwnerRef | null;
  /** 프로그램 진도표 등에서 learning_text가 바뀌었을 때 연간진도표 오버레이도 즉시 반영 */
  syncAnnualLearningTextOverlay?: boolean;
}): void {
  const ls = slideStorage();
  const ref = (params.presentationRefId ?? '').trim();
  const own = params.owner;
  const ak = (params.annualKey ?? '').trim();
  const payload = {
    workspaceId: params.workspaceId,
    scopeId: params.scopeId ?? '',
    learningText: params.learningText,
    updatedAt: Date.now(),
    ...(ak ? { annualKey: ak } : {}),
    ...(ref ? { presentationRefId: ref } : {}),
    ...(params.syncAnnualLearningTextOverlay ? { syncAnnualLearningTextOverlay: true } : {}),
    ...(own?.id?.trim()
      ? { owner: { kind: own.kind, id: own.id.trim() } as ProgramContentSlideOwnerRef }
      : {}),
  };
  writeProgramContentSlideLearningOverlay({
    workspaceId: params.workspaceId,
    annualKey: ak || undefined,
    learningText: params.learningText,
    presentationRefId: ref || undefined,
    owner: own,
  });
  try {
    ls?.setItem(PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_EVENT, { detail: payload }));
    }
  } catch {
    /* ignore */
  }
}

/** CRA `PUBLIC_URL`(서브경로 배포) 고려 */
export function buildProgramContentSlideUrl(
  id: string,
  opts?: { tutorialId?: string; tutorialStep?: number }
): string {
  const basePath = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const params = new URLSearchParams({
    k: id,
    mode: 'edit',
  });
  const tutorialId = (opts?.tutorialId ?? '').trim();
  if (tutorialId) {
    params.set('tutorial', tutorialId);
    if (typeof opts?.tutorialStep === 'number' && Number.isFinite(opts.tutorialStep)) {
      params.set('tutorialStep', String(Math.max(0, Math.floor(opts.tutorialStep))));
    }
  }
  const path = `${basePath}/program-content-slide?${params.toString()}`;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${path}`;
  }
  return path;
}

export type OpenProgramContentSlideFailureReason = 'stash_failed' | 'popup_blocked';

export type OpenProgramContentSlideResult =
  | { ok: true }
  | { ok: false; reason: OpenProgramContentSlideFailureReason };

/** 사용자 클릭 직후 호출 — async DB 조회 뒤 `openProgramContentSlideInNewTab`에 넘긴다 */
export function openProgramContentSlideTabPlaceholder(status?: 'ai-generating'): Window | null {
  // 듀얼 모니터면 다른 화면에 창으로 (아니면 기존처럼 새 탭)
  return openPresentationWindow(buildAppWindowPlaceholderUrl(status));
}

export function closeProgramContentSlideTabPlaceholder(tab: Window | null | undefined): void {
  try {
    tab?.close();
  } catch {
    /* ignore */
  }
}

export function openProgramContentSlideFailureMessage(
  reason: OpenProgramContentSlideFailureReason
): string {
  if (reason === 'stash_failed') {
    return '프레젠테이션 탭을 열 수 없습니다. 브라우저 저장 공간을 비운 뒤 다시 시도해 주세요.';
  }
  return '프레젠테이션 탭이 차단되었습니다. 브라우저 팝업 허용 후 다시 시도해 주세요.';
}

/** 메인 프레젠에서 같은 창으로 수업 프레젠을 연 경우 표시(닫기 → 메인 프레젠으로 돌아가기) */
export const PROGRAM_CONTENT_SLIDE_RETURN_TO_MAIN_KEY = 'cp:program-content-slide-return-to-main:v1';

export function isProgramContentSlideOpenedFromMainInSameWindow(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(PROGRAM_CONTENT_SLIDE_RETURN_TO_MAIN_KEY) === window.location.search;
  } catch {
    return false;
  }
}

/** 새 창/탭 대신 지금 창에서 수업 프레젠으로 이동(브라우저 뒤로가기로 메인 프레젠 복귀) */
export function openProgramContentSlideInSameWindow(payload: ProgramContentSlidePayloadV1): OpenProgramContentSlideResult {
  const id = stashProgramContentSlidePayload(payload, typeof window !== 'undefined' ? window : null);
  if (!id.trim()) return { ok: false, reason: 'stash_failed' };
  const url = buildProgramContentSlideUrl(id);
  try {
    const search = new URL(url, window.location.href).search;
    window.sessionStorage.setItem(PROGRAM_CONTENT_SLIDE_RETURN_TO_MAIN_KEY, search);
  } catch {
    /* ignore */
  }
  window.location.assign(url);
  return { ok: true };
}

export function openProgramContentSlideInNewTab(
  payload: ProgramContentSlidePayloadV1,
  options?: {
    preOpenedWindow?: Window | null;
    tutorialId?: string;
    tutorialStep?: number;
  }
): OpenProgramContentSlideResult {
  const preOpened = options?.preOpenedWindow ?? null;
  const id = stashProgramContentSlidePayload(payload, preOpened);
  if (!id.trim()) {
    closeProgramContentSlideTabPlaceholder(preOpened);
    return { ok: false, reason: 'stash_failed' };
  }
  const url = buildProgramContentSlideUrl(id, {
    tutorialId: options?.tutorialId,
    tutorialStep: options?.tutorialStep,
  });
  if (preOpened) {
    try {
      if (preOpened.closed) {
        closeProgramContentSlideTabPlaceholder(preOpened);
        return { ok: false, reason: 'popup_blocked' };
      }
      preOpened.location.replace(url);
      preOpened.focus?.();
      return { ok: true };
    } catch {
      closeProgramContentSlideTabPlaceholder(preOpened);
      const fallback = openPresentationWindow(url);
      if (!fallback) return { ok: false, reason: 'popup_blocked' };
      return { ok: true };
    }
  }
  const w = openPresentationWindow(url);
  if (!w) return { ok: false, reason: 'popup_blocked' };
  return { ok: true };
}
